/**
 * TanStack Query hooks for the admin products surface (STORY-008-007).
 *
 * `useProducts`  — `useInfiniteQuery` over the cursor-paginated admin list.
 * `useProduct`   — `useQuery` for the drawer detail view.
 * `usePatchProduct` / `useSoftDeleteProduct` / `useBulkSetStock` /
 * `useBulkSoftDelete` — `useMutation` wrappers that invalidate the list +
 *   detail caches on success.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  bulkSetAttribute,
  bulkSetStock,
  bulkSoftDelete,
  createProduct,
  createVariant,
  deleteVariant,
  getProduct,
  listProducts,
  listVariants,
  patchProduct,
  productKeys,
  reorderPhotos,
  searchSellers,
  softDeleteProduct,
  type AdminProduct,
  type AdminProductCreate,
  type AdminProductListResponse,
  type AdminProductUpdate,
  type AdminVariant,
  type AdminVariantListResponse,
  type BulkAttributeValue,
  type BulkResultResponse,
  type BulkSetAttributeResponse,
  type SearchSellersResponse,
} from "@/features/admin/products/api";

// SCRUM-165: exported so `ProductListPage`'s client-side numbered-pagination
// chunking uses the EXACT same page size as the underlying cursor fetch —
// "page N" in the UI always corresponds 1:1 to the Nth network page.
export const PAGE_LIMIT = 25;

/**
 * Items-per-page choices for the admin products list (SCRUM-241).
 *
 * These resize the CLIENT-side chunk, not the request. The list endpoint is
 * keyset/cursor paginated (Addendum §6.4) with no total count and no arbitrary
 * `page=N`; SCRUM-165 chunks accumulated cursor pages rather than converting it
 * to offset/limit, which would break page stability under concurrent inserts
 * for every consumer of `repo.list_products`. A larger size simply means more
 * cursor pages are gathered before one screen is drawn.
 *
 * Every value is a whole multiple of `PAGE_LIMIT` on purpose: a size that
 * straddled a fetch boundary would render a partial page until the next cursor
 * fetch landed, which reads to an admin as missing rows.
 */
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 500] as const;

/** The smallest option. The control exists so an admin can opt UP — raising
 * this would make every admin's first paint heavier without them asking. */
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

export interface ProductFilters {
  /** Server-side `stock_state` filter; null = all. */
  stockState: string | null;
  /** Server-side `category_id` filter; null = all categories. */
  categoryId: string | null;
}

export const DEFAULT_PRODUCT_FILTERS: ProductFilters = {
  stockState: null,
  categoryId: null,
};

export function useProducts(
  filters: ProductFilters = DEFAULT_PRODUCT_FILTERS,
): UseInfiniteQueryResult<InfiniteData<AdminProductListResponse>, Error> {
  return useInfiniteQuery<
    AdminProductListResponse,
    Error,
    InfiniteData<AdminProductListResponse>,
    ReturnType<typeof productKeys.list>,
    string | null
  >({
    queryKey: productKeys.list({
      stockState: filters.stockState,
      categoryId: filters.categoryId,
    }),
    queryFn: ({ pageParam }) =>
      listProducts({
        stock_state: filters.stockState,
        category_id: filters.categoryId,
        cursor: pageParam,
        limit: PAGE_LIMIT,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    staleTime: 10_000,
  });
}

export function useProduct(
  id: string | null,
): UseQueryResult<AdminProduct, Error> {
  return useQuery<AdminProduct, Error>({
    queryKey: productKeys.detail(id ?? ""),
    queryFn: () => getProduct(id!),
    enabled: id !== null,
    staleTime: 0,
  });
}

function useInvalidateProducts(): (id?: string) => Promise<void> {
  const qc = useQueryClient();
  return async (id?: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["admin", "products"] }),
      ...(id ? [qc.invalidateQueries({ queryKey: productKeys.detail(id) })] : []),
    ]);
  };
}

export function usePatchProduct(): UseMutationResult<
  AdminProduct,
  Error,
  { id: string; body: AdminProductUpdate }
> {
  const invalidate = useInvalidateProducts();
  return useMutation<AdminProduct, Error, { id: string; body: AdminProductUpdate }>({
    mutationFn: ({ id, body }) => patchProduct(id, body),
    onSuccess: (_data, { id }) => invalidate(id),
  });
}

export function useSoftDeleteProduct(): UseMutationResult<
  AdminProduct,
  Error,
  string
> {
  const invalidate = useInvalidateProducts();
  return useMutation<AdminProduct, Error, string>({
    mutationFn: (id) => softDeleteProduct(id),
    onSuccess: (_data, id) => invalidate(id),
  });
}

export function useBulkSetStock(): UseMutationResult<
  BulkResultResponse,
  Error,
  { ids: string[]; stockState: string }
> {
  const invalidate = useInvalidateProducts();
  return useMutation<BulkResultResponse, Error, { ids: string[]; stockState: string }>({
    mutationFn: ({ ids, stockState }) => bulkSetStock(ids, stockState),
    onSuccess: () => invalidate(),
  });
}

export function useBulkSoftDelete(): UseMutationResult<
  BulkResultResponse,
  Error,
  string[]
> {
  const invalidate = useInvalidateProducts();
  return useMutation<BulkResultResponse, Error, string[]>({
    mutationFn: (ids) => bulkSoftDelete(ids),
    onSuccess: () => invalidate(),
  });
}

export interface BulkSetAttributeVars {
  ids: string[];
  fieldSlug: string;
  value: BulkAttributeValue;
  unit?: string | null;
}

export function useBulkSetAttribute(): UseMutationResult<
  BulkSetAttributeResponse,
  Error,
  BulkSetAttributeVars
> {
  const invalidate = useInvalidateProducts();
  return useMutation<BulkSetAttributeResponse, Error, BulkSetAttributeVars>({
    mutationFn: ({ ids, fieldSlug, value, unit }) => bulkSetAttribute(ids, fieldSlug, value, unit),
    onSuccess: () => invalidate(),
  });
}

// ─────────────────────────── Task F3 additions ─────────────────────────────
// Admin `EditorPort` support: create, photo reorder, seller search, variants.
// `usePatchProduct` above is reused unchanged — its TYPE automatically
// widens once `AdminProductUpdate` gains the new optional fields (Task F3
// step 1b); no implementation change needed. There is deliberately no new
// "detail" hook — `useProduct(id)` already returns the rich detail shape
// (Task B3), so the editor reuses it directly.

export function useCreateProduct(): UseMutationResult<
  AdminProduct,
  Error,
  AdminProductCreate
> {
  const invalidate = useInvalidateProducts();
  return useMutation<AdminProduct, Error, AdminProductCreate>({
    mutationFn: (body) => createProduct(body),
    onSuccess: () => invalidate(),
  });
}

export function useReorderPhotos(): UseMutationResult<
  AdminProduct,
  Error,
  { productId: string; orderedImageIds: string[] }
> {
  return useMutation<
    AdminProduct,
    Error,
    { productId: string; orderedImageIds: string[] }
  >({
    mutationFn: ({ productId, orderedImageIds }) =>
      reorderPhotos(productId, orderedImageIds),
  });
}

export function useSearchSellers(
  q: string,
): UseQueryResult<SearchSellersResponse, Error> {
  return useQuery<SearchSellersResponse, Error>({
    queryKey: ["admin", "sellers", "search", q],
    queryFn: () => searchSellers(q),
    staleTime: 10_000,
  });
}

export function useVariants(
  productId: string | null,
): UseQueryResult<AdminVariantListResponse, Error> {
  return useQuery<AdminVariantListResponse, Error>({
    queryKey: ["admin", "products", "variants", productId ?? ""],
    queryFn: () => listVariants(productId!),
    enabled: productId !== null,
    staleTime: 0,
  });
}

export function useCreateVariant(): UseMutationResult<
  AdminVariant,
  Error,
  { productId: string; key: string; value: string }
> {
  const qc = useQueryClient();
  return useMutation<
    AdminVariant,
    Error,
    { productId: string; key: string; value: string }
  >({
    mutationFn: ({ productId, key, value }) => createVariant(productId, key, value),
    onSuccess: (_data, { productId }) =>
      qc.invalidateQueries({ queryKey: ["admin", "products", "variants", productId] }),
  });
}

export function useDeleteVariant(): UseMutationResult<
  void,
  Error,
  { productId: string; variantId: string }
> {
  const qc = useQueryClient();
  return useMutation<void, Error, { productId: string; variantId: string }>({
    mutationFn: ({ productId, variantId }) => deleteVariant(productId, variantId),
    onSuccess: (_data, { productId }) =>
      qc.invalidateQueries({ queryKey: ["admin", "products", "variants", productId] }),
  });
}
